import { ref, computed, watch, onMounted, onBeforeUnmount, type Ref } from 'vue';

import { restrictScroll, unleashScroll, saveActiveElement } from '@sg-core/resources/assets/js/utils';

export interface UseLightboxOptions {
  id: string;
  itemCount: Ref<number>;
  loop: Ref<boolean>;
  modelValue?: Ref<number | null | undefined>;
  onOpen?: () => void;
  onClose?: () => void;
}

export interface UseLightboxReturn {
  isOpen: Ref<boolean>;
  currentIndex: Ref<number>;
  open: (index?: number) => void;
  close: () => void;
  goTo: (index: number) => void;
  prev: () => void;
  next: () => void;
  canGoPrev: Ref<boolean>;
  canGoNext: Ref<boolean>;
}

export function useLightbox(options: UseLightboxOptions): UseLightboxReturn {
  const { id, itemCount, loop, modelValue, onOpen, onClose } = options;

  const isOpen = ref(false);
  const currentIndex = ref(0);
  let restoreActiveElement: (() => void) | null = null;

  const canGoPrev = computed(() => {
    if (itemCount.value <= 1) {
      return false;
    }

    return loop.value || currentIndex.value > 0;
  });

  const canGoNext = computed(() => {
    if (itemCount.value <= 1) {
      return false;
    }

    return loop.value || currentIndex.value < itemCount.value - 1;
  });

  function clampIndex(index: number): number {
    const count = itemCount.value;

    if (count === 0) {
      return 0;
    }

    if (index < 0) {
      return loop.value ? count - 1 : 0;
    }

    if (index >= count) {
      return loop.value ? 0 : count - 1;
    }

    return index;
  }

  function open(index = 0): void {
    if (isOpen.value) {
      currentIndex.value = clampIndex(index);
      return;
    }

    currentIndex.value = clampIndex(index);
    restoreActiveElement = saveActiveElement();
    isOpen.value = true;
    restrictScroll();
    onOpen?.();
  }

  function close(): void {
    if (!isOpen.value) {
      return;
    }

    isOpen.value = false;
    unleashScroll();
    restoreActiveElement?.();
    restoreActiveElement = null;
    onClose?.();
  }

  function goTo(index: number): void {
    currentIndex.value = clampIndex(index);
  }

  function prev(): void {
    if (!canGoPrev.value) {
      return;
    }

    goTo(currentIndex.value - 1);
  }

  function next(): void {
    if (!canGoNext.value) {
      return;
    }

    goTo(currentIndex.value + 1);
  }

  // Sync with modelValue if provided (external v-model)
  if (modelValue) {
    watch(modelValue, (val) => {
      if (val != null && val !== currentIndex.value) {
        goTo(val);
      }
    });
  }

  // Document-level click listener for `data-lightbox-open` triggers
  function handleTriggerClick(event: MouseEvent): void {
    const target = event.target;

    if (!(target instanceof Element)) {
      return;
    }

    if (target.closest('[data-lightbox-ignore]')) {
      return;
    }

    const trigger = target.closest(`[data-lightbox-open="${id}"]`);

    if (!trigger) {
      return;
    }

    const indexAttr = trigger.getAttribute('data-lightbox-index');
    const index = indexAttr != null ? parseInt(indexAttr, 10) : 0;
    open(isNaN(index) ? 0 : index);
  }

  onMounted(() => {
    document.addEventListener('click', handleTriggerClick);
  });

  onBeforeUnmount(() => {
    document.removeEventListener('click', handleTriggerClick);
    if (isOpen.value) {
      unleashScroll();
      restoreActiveElement?.();
    }
  });

  return {
    isOpen,
    currentIndex,
    open,
    close,
    goTo,
    prev,
    next,
    canGoPrev,
    canGoNext,
  };
}
